import { FunctionComponent, useEffect, useState } from "react";
import { Box, Button, LinearProgress, Stack, Typography } from "@mui/material";

import { api } from "../../api";
import { BulkAddStepProps } from "../BulkAddDialogTypes";
import { useGlobalSnackbar } from "../../consecutive-snackbar/GlobalSnackbar";
import { getMultiLevelPropertyValue } from "../../../utils/getMultiLevelPropertyValue";
import { importProgress } from "../generators/importProgress";

export const TrackProcess: FunctionComponent<BulkAddStepProps> = ({
  model,
  onNext,
  importId,
}) => {
  const { simple } = useGlobalSnackbar();
  const [isCanceled, setIsCanceled] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [progressNumerator, setProgressNumerator] = useState(0); // X
  const [progressDenominator, setProgressDenominator] = useState(0); // Y

  // Reset state only when importId changes
  useEffect(() => {
    setIsDone(false);
    setProgressNumerator(0);
    setProgressDenominator(0);
    setIsProcessing(false);
    setIsCanceled(false);
  }, [importId]);

  useEffect(() => {
    let cancelled = false;
    if (!importId || isDone) return;
    (async () => {
      try {
        setIsProcessing(true);
        for await (const update of importProgress(importId, () => cancelled)) {
          if (cancelled) break;
          setProgressNumerator(update.numerator ?? 0);
          setProgressDenominator(update.denominator ?? 0);
          if (update.done) {
            setIsDone(true);
            setIsProcessing(false);
            break;
          }
        }
      } catch (error) {
        let message = "Unknown network error";
        if (error instanceof Error) {
          const errorMessage = getMultiLevelPropertyValue(
            error,
            "response.data.error.message"
          );
          message = errorMessage ? errorMessage : error.message;
        }
        simple(message, { severity: "error" });
      } finally {
        setIsProcessing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [importId, isDone, simple, onNext]);

  const handleCancel = async () => {
    if (!importId) return;
    try {
      setIsCanceled(true);
      await api.imports.cancelImport({ importId });
    } catch (error) {
      let message = "Unknown network error";
      if (error instanceof Error) {
        const errorMessage = getMultiLevelPropertyValue(
          error,
          "response.data.error.message"
        );
        message = errorMessage ? errorMessage : error.message;
      }
      simple(message, { severity: "error" });
      setIsCanceled(false);
    }
  };

  return (
    <Box
      css={(theme) => ({
        padding: theme.spacing(2),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 220,
      })}
    >
      <Typography
        css={(theme) => ({
          padding: `0 ${theme.spacing(2)} ${theme.spacing(2)}`,
          textAlign: "center",
        })}
      >
        Processing your {model}…
      </Typography>
      <Box
        css={(theme) => ({
          width: "100%",
          maxWidth: 400,
          padding: `0 ${theme.spacing(2)} ${theme.spacing(2)}`,
        })}
      >
        <LinearProgress
          variant="determinate"
          value={
            progressDenominator > 0
              ? (progressNumerator / progressDenominator) * 100
              : 0
          }
        />
        <Typography
          variant="caption"
          sx={{
            mt: 1,
            display: "block",
            textAlign: "center",
          }}
        >
          {progressDenominator > 0
            ? `${progressNumerator} of ${progressDenominator} steps complete`
            : ""}
        </Typography>
      </Box>
      <Stack
        spacing={2}
        direction="row"
        justifyContent="center"
        css={(theme) => ({ padding: `0 ${theme.spacing(2)}` })}
      >
        <Button disableElevation onClick={handleCancel} disabled={isCanceled}>
          CANCEL
        </Button>
        <Button
          color="primary"
          onClick={onNext}
          disableElevation
          variant="contained"
          disabled={!isDone || isProcessing}
        >
          NEXT
        </Button>
      </Stack>
    </Box>
  );
};
